/**
 * useChatStream Hook (Web)
 *
 * Thin wrapper around shared useChatStreamCore.
 * Injects: web fetch (cookies), react-router navigation, CustomEvent preferences.
 */

import {
  type UseChatStreamResult,
  useChatStreamCore,
} from '@parlay-picker/shared/hooks/useChatStreamCore';
import { useChatStore } from '@stores/chatStore';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAwareFetch } from '../contexts/authHttpUtils';

export type { UseChatStreamResult };

export function useChatStream(): UseChatStreamResult {
  const navigate = useNavigate();
  const isStreaming = useChatStore((s) => s.isStreaming);

  const apiFetch = useCallback((url: string, init?: RequestInit) => authAwareFetch(url, init), []);

  const handleNavigation = useCallback(
    (screen: string, _context?: Record<string, unknown>) => {
      const validTabs = ['picks', 'parlays', 'lab', 'leaderboard', 'search', 'feed', 'admin'];

      if (validTabs.includes(screen)) {
        window.dispatchEvent(new CustomEvent('chat-navigate-tab', { detail: { tab: screen } }));
      } else if (screen === 'analysis_config' || screen === 'ban_list') {
        useGlobalShortcutsStore.getState().openConfigPanel();
      } else if (screen === 'settings' || screen === 'subscription') {
        window.dispatchEvent(new CustomEvent('chat-navigate-tab', { detail: { tab: 'settings' } }));
      } else if (screen === 'analytics') {
        window.dispatchEvent(new CustomEvent('chat-navigate-tab', { detail: { tab: 'lab' } }));
      } else if (screen === 'parlay_builder') {
        window.dispatchEvent(new CustomEvent('chat-navigate-tab', { detail: { tab: 'picks' } }));
        useParlayBuilderStore.getState().setExpanded(true);
      } else if (screen === 'notifications') {
        window.dispatchEvent(new CustomEvent('chat-navigate-tab', { detail: { tab: 'feed' } }));
      } else if (screen === 'profile') {
        navigate('/profile');
      }
    },
    [navigate],
  );

  const handlePreferenceUpdate = useCallback((key: string, value: string) => {
    if (key === 'theme') {
      window.dispatchEvent(new CustomEvent('chat-update-theme', { detail: value }));
    }
    if (key === 'sportsbook') {
      window.dispatchEvent(new CustomEvent('chat-update-sportsbook', { detail: value }));
    }
  }, []);

  return useChatStreamCore({
    apiFetch,
    onNavigate: handleNavigation,
    onPreferenceUpdate: handlePreferenceUpdate,
    getChatStore: () => useChatStore.getState(),
    isStreaming,
  });
}
